import { numberFormatter } from '../i18n';
/**
 * Localised Number Formatting Utilities
 * Provides consistent formatting for numbers, percentages, and currencies
 * using the current presentation locale (e.g., 12.345,67 for twelve thousand three hundred forty-five point sixty-seven)
 */

/**
 * Format a number with current locale conventions
 * @param {number} value - The number to format
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 0)
 * @param {boolean} options.forceDecimals - Always show decimal places even if zero (default: false)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '-';

  const { decimals = 0, forceDecimals = false } = options;

  return numberFormatter({
    minimumFractionDigits: forceDecimals ? decimals : 0,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format a currency value in EUR with current locale conventions
 * @param {number} value - The value to format
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 0)
 * @param {boolean} options.compact - Use compact notation for large numbers (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '-';

  const { decimals = 0, compact = false } = options;

  if (compact) {
    if (Math.abs(value) >= 1000000) {
      return `€${formatNumber(value / 1000000, { decimals: 1, forceDecimals: true })}M`;
    }
    if (Math.abs(value) >= 1000) {
      return `€${formatNumber(value / 1000, { decimals: 0 })}K`;
    }
  }

  return numberFormatter({
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format currency in compact notation (for chart axes)
 * @param {number} value - The value to format
 * @returns {string} Compact formatted currency string
 */
export const formatCurrencyCompact = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';

  if (Math.abs(value) >= 1000000) {
    return `€${formatNumber(value / 1000000, { decimals: 1, forceDecimals: true })}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `€${formatNumber(value / 1000, { decimals: 0 })}K`;
  }
  return `€${formatNumber(value, { decimals: 0 })}`;
};

/**
 * Format a decimal value as a percentage with current locale conventions
 * @param {number} value - The decimal value (e.g., 0.3 for 30%)
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 1)
 * @param {boolean} options.forceDecimals - Always show decimal places (default: false)
 * @param {boolean} options.alreadyPercent - If true, value is already in percent form (default: false)
 * @returns {string} Formatted percentage string (e.g., "30%" or "30,5%")
 */
export const formatPercentage = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '-';

  const { decimals = 1, forceDecimals = false, alreadyPercent = false } = options;

  const percentValue = alreadyPercent ? value : value * 100;

  const formattedValue = numberFormatter({
    minimumFractionDigits: forceDecimals ? decimals : 0,
    maximumFractionDigits: decimals,
  }).format(percentValue);

  return `${formattedValue}%`;
};

/**
 * Format a decimal number with current locale conventions (comma as decimal separator)
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted decimal string
 */
export const formatDecimal = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '-';

  return numberFormatter({
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format year number (simple integer)
 * @param {number} value - The year number
 * @returns {string} Formatted year string
 */
export const formatYear = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return Math.round(value).toString();
};

/**
 * Format a range of years
 * @param {number} start - Start year
 * @param {number} end - End year
 * @returns {string} Formatted year range
 */
export const formatYearRange = (start, end) => {
  if (start === null || end === null) return '-';
  return `${formatYear(start)} - ${formatYear(end)}`;
};

/**
 * Format a number for display in input fields (Italian locale)
 * @param {number|string} value - The value to format
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 0)
 * @param {boolean} options.isPercent - If true, add % suffix (default: false)
 * @returns {string} Formatted string for display
 */
export const formatInputDisplay = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';

  const { decimals = 0, isPercent = false } = options;

  const formatted = numberFormatter({
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(numValue);

  return isPercent ? `${formatted}%` : formatted;
};

/**
 * Parse an Italian-formatted number string to a numeric value
 *
 * ITALIAN NUMBER FORMAT RULES:
 * - Dot (.) is ALWAYS the thousands separator
 * - Comma (,) is ALWAYS the decimal separator
 *
 * Examples:
 * - "70.000 €" → 70000
 * - "2.500.000" → 2500000
 * - "7,5" → 7.5
 * - "12,0%" → 12.0
 * - "1.234,56" → 1234.56
 * - "1234" → 1234
 *
 * @param {string} value - The string to parse
 * @returns {number|null} Parsed number or null if invalid
 */
export const parseItalianNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;

  // Remove currency symbols (€), percentage symbols (%), and whitespace
  let cleanValue = String(value).trim().replace(/[€%\s]/g, '');

  // If empty after cleaning, return null
  if (cleanValue === '') return null;

  // ITALIAN FORMAT: dots are ALWAYS thousands separators, commas are ALWAYS decimal separators
  // Step 1: Remove all dots (thousands separators)
  cleanValue = cleanValue.replace(/\./g, '');

  // Step 2: Replace comma with dot (decimal separator)
  cleanValue = cleanValue.replace(',', '.');

  const result = parseFloat(cleanValue);
  return isNaN(result) ? null : result;
};

export default {
  formatNumber,
  formatCurrency,
  formatCurrencyCompact,
  formatPercentage,
  formatDecimal,
  formatYear,
  formatYearRange,
  formatInputDisplay,
  parseItalianNumber,
};
